import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { LocalStorageService } from '@services'
import { OverlayPayload, useOverlayActions } from '@store'

type TriggerFactory = () => OverlayPayload[]

const OVERLAY_TRIGGER_KEY = 'overlayTriggers'

type OverlayTriggersLCFields = {
  [key: string]: boolean
}

export const useOverlayTrigger = () => {
  const { setOverlayQueue } = useOverlayActions()
  const overlayActions = useOverlayActions()
  const navigate = useNavigate()

  const goHome = () => {
    navigate('/home')
  }

  const overlayTriggers: Record<string, TriggerFactory> = useMemo(
    () => ({
      questWithDialogCompleted: () => [
        {
          type: 'info',
          payload: {
            title: 'questCompleteOverlay.title',
            image:
              'https://not-platform.fra1.cdn.digitaloceanspaces.com/glance/quest_completed.png',
            icon: 'spiral',
            buttonText: 'shared.close',
            onClose: () => {
              overlayActions.hideOverlay()
            },
          },
        },
      ],
      questCompleted: () => [
        {
          type: 'info',
          payload: {
            title: 'questCompleteOverlay.title',
            image:
              'https://not-platform.fra1.cdn.digitaloceanspaces.com/glance/quest_completed.png',
            icon: 'spiral',
            buttonText: 'questCompleteOverlay.accept',
            hideCloseButton: true,
            onClose: () => {
              overlayActions.hideOverlay()
              goHome()
            },
          },
        },
      ],
      spiralQuestClick_1: () => [
        {
          type: 'chapter',
          payload: {
            text: 'chapter.stage1.text',
            title: 'chapter.stage1.title',
            onClose: () => {
              overlayActions.hideOverlay()
            },
          },
        },
        {
          type: 'narrator',
          payload: {
            text: 'narrator.Aeron-07',
            soundPhrase: '/sounds/narrator/Prologue_Aeron_full_Good_07.mp3',
            onClose: () => {
              overlayActions.hideOverlay()
            },
          },
        },
      ],
      nightsong: () => [
        {
          type: 'narrator',
          payload: {
            text: 'narrator.Aeron-08',
            soundPhrase: '/sounds/narrator/Prologue_Aeron_full_Good_08.mp3',
            onClose: () => {
              overlayActions.hideOverlay()
            },
          },
        },
      ],
    }),
    [overlayActions]
  )

  const triggerOverlay = useCallback(
    (
      key: string,
      overlayPayload?: Exclude<OverlayPayload[], null>,
      force?: boolean
    ) => {
      const triggeredKeys: OverlayTriggersLCFields | null =
        LocalStorageService.getItem(OVERLAY_TRIGGER_KEY)

      if (!force && triggeredKeys?.[key]) return
      const payloadToShow = overlayPayload ?? overlayTriggers[key]?.()

      if (!payloadToShow || payloadToShow.length === 0) return

      const markTriggered = () => {
        const current: OverlayTriggersLCFields | null =
          LocalStorageService.getItem(OVERLAY_TRIGGER_KEY) ?? {}
        LocalStorageService.setItem(OVERLAY_TRIGGER_KEY, {
          ...current,
          [key]: true,
        })
      }

      const wrappedPayloads: OverlayPayload[] = payloadToShow.map((p, idx) => {
        const isLast = idx === payloadToShow.length - 1

        if (!isLast) {
          return {
            ...p,
            payload: p.payload ? { ...p.payload } : undefined,
          } as OverlayPayload
        }

        const originalOnClose = (p.payload as any)?.onClose as
          | ((params: { isComplete: boolean }) => void)
          | undefined

        const newOnClose = (params: { isComplete: boolean }) => {
          try {
            originalOnClose && originalOnClose(params)
          } finally {
            markTriggered()
          }
        }

        return {
          ...p,
          payload: p.payload
            ? {
                ...p.payload,
                onClose: newOnClose,
              }
            : { onClose: newOnClose },
        } as OverlayPayload
      })

      setOverlayQueue(wrappedPayloads)
    },
    [setOverlayQueue, overlayTriggers]
  )

  return { triggerOverlay }
}
